import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import {
    Box,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";

import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";

import CustomForm from "../../../helper-components/form/CustomForm.jsx";
import NavigationFooter from "../../../helper-components/misc/NavigationFooter.jsx";
import PaperFragment from "../../../helper-components/misc/PaperFragment.jsx";
import PaperText from "../../../helper-components/misc/PaperText.jsx";
import CustomButton from "../../../helper-components/input/CustomButton.jsx";

import ExaminationCompare from "../examinations/components/ExaminationCompare.jsx";
import PlaqueAndBleedingChart from "../examinations/components/PlaqueAndBleedingChart.jsx";

import { formatDate } from "../../../helper-functions/shared/miscUtils.js";
import { getPatientDisplayName } from "../../../helper-functions/shared/patientDisplay.js";
import {
    annotateComparisonWithHistory,
    buildComparisonNarrative,
    compareExaminations,
    examinationsThroughFollowup,
    formatComparisonSiteLine,
    isDefaultComparisonFilter
} from "../../../helper-functions/shared/chartUtils.js";
import { queryPatient } from "../../../helper-functions/api/patientQueries.js";
import { fetchExaminationById, queryAllPatientExaminations } from "../../../helper-functions/api/examinationQueries.js";

const sortExamsAsc = (exams) => [...exams].sort((a, b) => new Date(a.created_on) - new Date(b.created_on));

export default function PatientCompare(props) {
    const { patientId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const [patient, setPatient] = React.useState(null);
    const [history, setHistory] = React.useState([]);
    const [fullExams, setFullExams] = React.useState([]);
    const [failedExamCount, setFailedExamCount] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [isExporting, setIsExporting] = React.useState(false);
    // The export canvas must be light regardless of the active theme; on screen the panel follows the theme.
    const [forceLightExport, setForceLightExport] = React.useState(false);
    const [showAllChanges, setShowAllChanges] = React.useState(false);

    const mode = searchParams.get("mode") || "diff";
    const baselineId = parseInt(searchParams.get("baseline") || "0", 10);
    const followupId = parseInt(searchParams.get("followup") || "0", 10);

    React.useEffect(() => {
        const id = parseInt(patientId, 10);
        queryPatient(props, { id });

        queryAllPatientExaminations(props, id, (summaryHistory) => {
            setHistory(summaryHistory);
            if (summaryHistory.length === 0) {
                setLoading(false);
                return;
            }

            let settled = 0;
            let failed = 0;
            const full = [];
            const markSettled = () => {
                settled += 1;
                if (settled === summaryHistory.length) {
                    setFullExams(sortExamsAsc(full));
                    setFailedExamCount(failed);
                    setLoading(false);
                }
            };

            summaryHistory.forEach((summary) => {
                fetchExaminationById(
                    props,
                    summary.id,
                    (exam) => {
                        full.push(exam);
                        markSettled();
                    },
                    () => {
                        failed += 1;
                        markSettled();
                    }
                );
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId]);

    React.useEffect(() => {
        const cached = props?.backend?.patients?.data?.find((entry) => entry.id === parseInt(patientId, 10));
        if (cached) setPatient(cached);
    }, [props?.backend?.patients?.data, patientId]);

    const sortedHistory = React.useMemo(() => sortExamsAsc(history), [history]);

    React.useEffect(() => {
        if (sortedHistory.length < 2 || baselineId || followupId) return;
        const baseline = sortedHistory[sortedHistory.length - 2].id;
        const followup = sortedHistory[sortedHistory.length - 1].id;
        setSearchParams({ baseline: String(baseline), followup: String(followup), mode }, { replace: true });
    }, [sortedHistory, baselineId, followupId, mode, setSearchParams]);

    const baselineExam = fullExams.find((exam) => exam.id === baselineId);
    const followupExam = fullExams.find((exam) => exam.id === followupId);

    let swapped = false;
    let orderedBaseline = baselineExam;
    let orderedFollowup = followupExam;
    if (baselineExam && followupExam && new Date(baselineExam.created_on) > new Date(followupExam.created_on)) {
        orderedBaseline = followupExam;
        orderedFollowup = baselineExam;
        swapped = true;
    }

    const comparison = React.useMemo(() => {
        if (!orderedBaseline?.teeth_data || !orderedFollowup?.teeth_data) return null;
        const result = compareExaminations(orderedFollowup.teeth_data, orderedBaseline.teeth_data);
        return annotateComparisonWithHistory(result, examinationsThroughFollowup(sortExamsAsc(fullExams), orderedFollowup.id));
    }, [orderedBaseline, orderedFollowup, fullExams]);

    const changedSites = React.useMemo(() => {
        if (!comparison) return [];
        const sites = comparison.sites.filter((site) => site.riskState !== "stable" && site.riskState !== "indeterminate");
        return showAllChanges ? sites : sites.filter(isDefaultComparisonFilter);
    }, [comparison, showAllChanges]);

    const updateSelection = (nextBaseline, nextFollowup, nextMode = mode) => {
        if (!nextBaseline || !nextFollowup || nextBaseline === nextFollowup) return;
        setSearchParams({ baseline: String(nextBaseline), followup: String(nextFollowup), mode: nextMode });
    };

    const handleExport = async (type) => {
        const element = document.getElementById("patient-compare-export");
        if (!element) return;

        setIsExporting(true);
        setForceLightExport(true);
        // Wait for the forced-light styles to be painted before html2canvas reads the DOM.
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

        try {
            const canvas = await html2canvas(element, {
                backgroundColor: "#ffffff",
                scale: 1.75,
                useCORS: true
            });

            if (type === "png") {
                const link = document.createElement("a");
                link.download = `comparison-${patientId}.png`;
                link.href = canvas.toDataURL("image/png");
                link.click();
            } else {
                const imgData = canvas.toDataURL("image/png");
                const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
                const width = canvas.width * ratio;
                const height = canvas.height * ratio;
                pdf.addImage(imgData, "PNG", (pageWidth - width) / 2, 10, width, height);
                pdf.save(`comparison-${patientId}.pdf`);
            }
        } finally {
            setForceLightExport(false);
            setIsExporting(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
                <PaperText value="Loading comparison, please wait..." center p={8} />
            </Container>
        );
    }

    if (history.length < 2) {
        return (
            <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
                <PaperText value="At least two examinations are required for comparison." center p={8} />
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <CustomForm
                        title="Compare examinations"
                        subtitle="Select a baseline and follow-up examination for the same patient."
                        icon={<CompareArrowsIcon />}
                        form={
                            <Stack direction={{ xs: "column", md: "row" }} spacing={2} mt={2} alignItems="center">
                                <FormControl size="small" sx={{ minWidth: 220 }}>
                                    <InputLabel>Baseline examination</InputLabel>
                                    <Select
                                        label="Baseline examination"
                                        value={baselineId || ""}
                                        onChange={(event) => updateSelection(parseInt(event.target.value, 10), followupId)}>
                                        {sortedHistory.map((exam) => (
                                            <MenuItem key={exam.id} value={exam.id}>
                                                {formatDate(exam.created_on)} | #{exam.id}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: 220 }}>
                                    <InputLabel>Follow-up examination</InputLabel>
                                    <Select
                                        label="Follow-up examination"
                                        value={followupId || ""}
                                        onChange={(event) => updateSelection(baselineId, parseInt(event.target.value, 10))}>
                                        {sortedHistory.map((exam) => (
                                            <MenuItem key={exam.id} value={exam.id}>
                                                {formatDate(exam.created_on)} | #{exam.id}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={mode}
                                    onChange={(event, value) => value && updateSelection(baselineId, followupId, value)}>
                                    <ToggleButton value="diff">Diff</ToggleButton>
                                    <ToggleButton value="sideBySide">Side by side</ToggleButton>
                                </ToggleButtonGroup>

                                <CustomButton {...props} title="PNG" size="small" startIcon={<ImageIcon />} onClick={() => handleExport("png")} disabled={isExporting} />
                                <CustomButton {...props} title="PDF" size="small" startIcon={<PictureAsPdfIcon />} onClick={() => handleExport("pdf")} disabled={isExporting} />
                            </Stack>
                        }
                    />
                </Grid>

                <Grid item xs={12}>
                    <PaperFragment
                        p={2}
                        value={
                            <Stack
                                spacing={2}
                                id="patient-compare-export"
                                sx={forceLightExport ? { backgroundColor: "#fff", color: "#111", p: 1 } : { backgroundColor: "background.paper", color: "text.primary", p: 1 }}>
                                <Typography variant="body2">
                                    Patient: {getPatientDisplayName(patient)} | Baseline: {formatDate(orderedBaseline?.created_on)} | Follow-up:{" "}
                                    {formatDate(orderedFollowup?.created_on)}
                                </Typography>

                                {swapped && (
                                    <Typography variant="body2" color="warning.main">
                                        The selected examinations were reversed to maintain chronological order (baseline before follow-up).
                                    </Typography>
                                )}

                                {failedExamCount > 0 && (
                                    <Typography variant="body2" color="warning.main">
                                        {failedExamCount} examination{failedExamCount === 1 ? "" : "s"} could not be loaded. Historic site labels may be incomplete.
                                    </Typography>
                                )}

                                {comparison && (
                                    <Typography variant="body2" aria-live="polite">
                                        {buildComparisonNarrative(comparison, formatDate(orderedBaseline?.created_on), formatDate(orderedFollowup?.created_on))}
                                    </Typography>
                                )}

                                {mode === "diff" && comparison && (
                                    <>
                                        <Stack direction="row" spacing={1}>
                                            <CustomButton
                                                {...props}
                                                size="small"
                                                title={showAllChanges ? "Show default filter" : "Show all changes"}
                                                onClick={() => setShowAllChanges((prev) => !prev)}
                                            />
                                        </Stack>

                                        <Box>
                                            {changedSites.map((site) => (
                                                <Typography key={`${site.position}-${site.site}`} variant="body2" sx={{ lineHeight: 1.8 }}>
                                                    {formatComparisonSiteLine(site)}
                                                    {site.historyState && site.historyState !== "insufficient_data" ? ` [${site.historyState.replace(/_/g, " ")}]` : ""}
                                                </Typography>
                                            ))}
                                        </Box>

                                        <ExaminationCompare
                                            teethData={orderedFollowup.teeth_data}
                                            previousTeethData={orderedBaseline.teeth_data}
                                            patientInfo={{
                                                name: getPatientDisplayName(patient),
                                                currentDate: formatDate(orderedFollowup?.created_on),
                                                previousDate: formatDate(orderedBaseline?.created_on)
                                            }}
                                        />
                                    </>
                                )}

                                {mode === "sideBySide" && orderedBaseline && orderedFollowup && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <PlaqueAndBleedingChart
                                                teethData={orderedBaseline.teeth_data}
                                                patientInfo={{
                                                    name: getPatientDisplayName(patient),
                                                    date: formatDate(orderedBaseline.created_on, { onlyDate: true })
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <PlaqueAndBleedingChart
                                                teethData={orderedFollowup.teeth_data}
                                                patientInfo={{
                                                    name: getPatientDisplayName(patient),
                                                    date: formatDate(orderedFollowup.created_on, { onlyDate: true })
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Stack>
                        }
                    />
                </Grid>

                <NavigationFooter
                    rows={1}
                    options={[
                        {
                            path: `/database/patients/${patientId}`,
                            title: "Back to patient",
                            variant: "outlined",
                            icon: <PermContactCalendarIcon />
                        }
                    ]}
                />
            </Grid>
        </Container>
    );
}
